/* eslint-disable react-hooks/exhaustive-deps */
import { iamUrl } from "../utils/urls";
import { useLazyFetch, useLazyPost, getHeaders } from "../utils/apiclient";
import { useContext, useEffect } from "react";
import { UserContext } from "../contexts";

export const useCreateGroup = (path) => {
  return useLazyPost(`${iamUrl}${path}/_iam_/groups`, {
    headers: { ...getHeaders(), Action: "CREATE" },
    method: "POST",
  });
};

export const useCreateRole = (path) => {
  return useLazyPost(`${iamUrl}${path}/_iam_/roles`, {
    headers: { ...getHeaders(), Action: "CREATE" },
    method: "POST",
  });
};

export const useDeleteGroup = (path) => {
  return useLazyFetch(`${iamUrl}${path}/_iam_/groups`, {
    headers: { ...getHeaders(), Action: "DELETE" },
    method: "DELETE",
  });
};

export const useDeleteRole = (path) => {
  return useLazyFetch(`${iamUrl}${path}/_iam_/roles`, {
    headers: { ...getHeaders(), Action: "DELETE" },
    method: "DELETE",
  });
};

export const useListAllGroups = (path) => {
  return useLazyFetch(`${iamUrl}${path}/_iam_/groups`, {
    headers: { ...getHeaders(), Action: "LIST_ALL" },
  });
};

export const useListAllRoles = (path) => {
  return useLazyFetch(`${iamUrl}${path}/_iam_/roles`, {
    headers: { ...getHeaders(), Action: "LIST_ALL" },
  });
};

export const useListAllTeams = () => {
  return useLazyFetch(`${iamUrl}/user-management/teams`, {
    headers: { ...getHeaders(), Action: "LIST_ALL" },
  });
};

export const useReadGroup = (path) => {
  return useLazyFetch(`${iamUrl}${path}/_iam_/groups`, {
    headers: { ...getHeaders(), Action: "READ" },
  });
};

export const useReadRole = (path) => {
  return useLazyFetch(`${iamUrl}${path}/_iam_/roles`, {
    headers: { ...getHeaders(), Action: "READ" },
  });
};

export const useUpdateGroupMembers = (path) => {
  return useLazyPost(`${iamUrl}${path}/_iam_/groups`, {
    headers: { ...getHeaders(), Action: "UPDATE_MEMBERS" },
    method: "PATCH",
  });
};

export const useUpdateRoleAssignments = (path) => {
  return useLazyPost(`${iamUrl}${path}/_iam_/roles`, {
    headers: { ...getHeaders(), Action: "UPDATE_ASSIGNMENTS" },
    method: "PATCH",
  });
};

export const useUpdateRolePermissions = (path) => {
  return useLazyPost(`${iamUrl}${path}/_iam_/roles`, {
    headers: { ...getHeaders(), Action: "UPDATE_PERMISSIONS" },
    method: "PATCH",
  });
};

// Like useHasPermission, but also reports whether the check is still in
// flight, so callers can avoid treating "not yet known" the same as "denied"
// (e.g. disabling a button while loading instead of flashing it as enabled).
export const useHasPermissionState = (
  path,
  method,
  action,
  dependencies = []
) => {
  const loggedInUser = useContext(UserContext);
  const { postData, responseStatus, isLoading, error } = useLazyPost(
    `${iamUrl}/http-requests`,
    {
      headers: { ...getHeaders(), Action: "CHECK_AUTHORIZATION" },
      method: "POST",
    }
  );
  useEffect(() => {
    if (!process.env.REACT_APP_AUTH_EMAIL) {
      postData({
        path,
        method,
        action,
        user: loggedInUser?.email,
      });
    }
  }, dependencies);
  if (process.env.REACT_APP_AUTH_EMAIL) {
    return { hasPermission: true, isLoading: false };
  }
  // responseStatus is only null before the first response has come back;
  // isLoading alone misses the brief window before the effect above fires.
  // A network-level failure also leaves responseStatus null, so without the
  // error check a failed check would stay "loading" forever instead of
  // resolving to denied.
  return {
    hasPermission: responseStatus === 200,
    isLoading: isLoading || (responseStatus === null && !error),
  };
};

export const useHasPermission = (path, method, action, dependencies = []) => {
  const { hasPermission } = useHasPermissionState(
    path,
    method,
    action,
    dependencies
  );
  return hasPermission;
};

export const useListResources = (path, method, action, dependencies = []) => {
  const loggedInUser = useContext(UserContext);
  const { postData, response } = useLazyPost(`${iamUrl}/resources`, {
    headers: { ...getHeaders(), Action: "LIST" },
    method: "POST",
  });

  useEffect(() => {
    if (!process.env.REACT_APP_AUTH_EMAIL) {
      postData({
        path,
        method,
        action,
        user: loggedInUser?.email,
      });
    }
  }, dependencies);
  if (process.env.REACT_APP_AUTH_EMAIL) {
    return ["*"];
  }
  return response?.resources || [];
};
